/**
 * Markdown è il formato di archiviazione dei contenuti wiki.
 *
 * L'editor resta WYSIWYG (contentEditable, quindi HTML) e la conversione avviene
 * ai bordi: `mdToHtml` quando il testo entra nell'editor o viene visualizzato,
 * `htmlToMd` quando viene salvato. Rispetto all'HTML salvato in precedenza si
 * guadagnano diff leggibili, export e ricerca sul testo reale; per non perdere
 * nulla, ciò che il Markdown non sa esprimere (embed video, sottolineato) resta
 * come HTML grezzo dentro il Markdown — che è legale e lo fa anche Obsidian.
 *
 * I body storici in HTML puro continuano a funzionare: markdown-it li lascia
 * passare come blocchi HTML e vengono convertiti in Markdown alla prima modifica.
 */
import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import DOMPurify from 'dompurify';

export interface WikiPageRef {
  slug: string;
  title: string;
  icon?: string | null;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// ───────────────────────── Markdown → HTML ─────────────────────────

/** Host consentiti per gli `<iframe>`: solo player video, niente altro. */
const EMBED_HOSTS = ['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'player.vimeo.com'];

const isAllowedEmbed = (src: string) => {
  try {
    return EMBED_HOSTS.includes(new URL(src, window.location.origin).hostname);
  } catch {
    return false;
  }
};

const md = new MarkdownIt({
  html: true, // necessario per embed e <u>; l'output passa comunque da DOMPurify
  linkify: false, // i link si inseriscono dal dialog: evita conversioni a sorpresa
  breaks: false,
  typographer: false,
});

/** `[[slug]]` e `[[slug|etichetta]]` → link interno alla wiki. */
md.inline.ruler.before('link', 'wikilink', (state, silent) => {
  const start = state.pos;
  if (state.src.charCodeAt(start) !== 0x5b || state.src.charCodeAt(start + 1) !== 0x5b) return false;
  const end = state.src.indexOf(']]', start + 2);
  if (end < 0) return false;
  const raw = state.src.slice(start + 2, end);
  if (!raw.trim() || raw.includes('[') || raw.includes('\n')) return false;
  if (!silent) {
    const [slug, label] = raw.split('|');
    const token = state.push('wikilink', '', 0);
    token.content = raw;
    token.meta = { slug: slug.trim(), label: (label ?? '').trim() };
  }
  state.pos = end + 2;
  return true;
});

md.renderer.rules.wikilink = (tokens, idx, _options, env) => {
  const { slug, label } = tokens[idx].meta as { slug: string; label: string };
  const page = (env as { bySlug?: Map<string, WikiPageRef> })?.bySlug?.get(slug);
  if (!page) {
    return `<a href="#" class="wiki-link is-broken" title="Pagina non trovata">${escapeHtml(label || slug)} ⚠</a>`;
  }
  const icon = page.icon ? `${escapeHtml(page.icon)} ` : '';
  return `<a href="#" data-wiki-slug="${escapeHtml(page.slug)}" class="wiki-link">${icon}${escapeHtml(label || page.title)}</a>`;
};

/**
 * `- [ ] voce` → la checklist dell'editor (`li.task` + `span.task-box`), così
 * il markup resta quello che i CSS conoscono già.
 */
md.core.ruler.after('inline', 'task_lists', (state) => {
  const tokens = state.tokens;
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].type !== 'list_item_open') continue;
    const inline = tokens[i + 1]?.type === 'inline' ? tokens[i + 1] : tokens[i + 2];
    if (!inline || inline.type !== 'inline') continue;
    const m = /^\[([ xX])\]\s+/.exec(inline.content);
    if (!m) continue;

    const done = m[1].toLowerCase() === 'x';
    inline.content = inline.content.slice(m[0].length);
    const first = inline.children?.[0];
    if (first && first.type === 'text') first.content = first.content.replace(/^\[([ xX])\]\s*/, '');

    tokens[i].attrJoin('class', done ? 'task is-done' : 'task');
    const box = new state.Token('html_inline', '', 0);
    box.content = `<span class="task-box${done ? ' is-on' : ''}"></span> `;
    inline.children?.unshift(box);
  }
  return true;
});

md.renderer.rules.table_open = () => '<table class="wiki-table">';

let purifyReady = false;
function ensurePurifyHooks() {
  if (purifyReady || typeof window === 'undefined') return;
  purifyReady = true;
  // Gli iframe sopravvivono solo se puntano a un player video conosciuto.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName !== 'IFRAME') return;
    const el = node as unknown as Element;
    if (!isAllowedEmbed(el.getAttribute('src') ?? '')) el.remove();
    else el.classList.add('md-embed');
  });
}

const PURIFY_CONFIG = {
  ADD_TAGS: ['iframe', 'video', 'source'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'controls', 'target', 'poster', 'playsinline'],
};

/**
 * Ripulisce HTML arbitrario mantenendo gli embed video leciti.
 * Sostituisce la vecchia sanificazione a regex (aggirabile) con DOMPurify.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  if (typeof window === 'undefined') return html;
  ensurePurifyHooks();
  return DOMPurify.sanitize(html, PURIFY_CONFIG) as unknown as string;
}

/** Markdown (o HTML legacy) → HTML sanificato, con i `[[link]]` risolti. */
export function mdToHtml(source: string, pages?: WikiPageRef[]): string {
  if (!source) return '';
  const bySlug = new Map((pages ?? []).map((p) => [p.slug, p]));
  return sanitizeHtml(md.render(source, { bySlug }));
}

/** Heuristica storica: body salvati prima del passaggio a Markdown. */
export const isHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s);

/** Aggiunge gli `id` agli heading per l'indice della pagina. */
export const headingId = (text: string) =>
  'h-' + text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const addHeadingIds = (html: string) =>
  html.replace(/<(h[1-6])(\s[^>]*)?>([\s\S]*?)<\/\1>/gi, (_m, tag, attrs, inner) =>
    `<${tag}${attrs ?? ''} id="${headingId(String(inner).replace(/<[^>]+>/g, ''))}">${inner}</${tag}>`);

/** Indice (h1–h3) estratto dall'HTML già renderizzato. */
export function tocFromHtml(html: string) {
  const out: { level: number; text: string; id: string }[] = [];
  const re = /<(h[1-3])[^>]*>([\s\S]*?)<\/\1>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) {
    const text = m[2].replace(/<[^>]+>/g, '').trim();
    if (text) out.push({ level: Number(m[1][1]), text, id: headingId(text) });
  }
  return out;
}

/** Testo visibile di un HTML, per conteggio parole e anteprime. */
export const htmlToText = (html: string) =>
  html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

// ───────────────────────── HTML → Markdown ─────────────────────────

const turndown = new TurndownService({
  headingStyle: 'atx',
  hr: '---',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
  strongDelimiter: '**',
  linkStyle: 'inlined',
  br: '  ',
});
turndown.use(gfm);

/** Impostato da `htmlToMd` per risolvere titolo/icona dei link interni. */
let serializePages = new Map<string, WikiPageRef>();

const block = (html: string) => `\n\n${html}\n\n`;

// Link interni → [[slug]] / [[slug|etichetta]] (l'icona non entra nel sorgente:
// viene riaggiunta in rendering dalla pagina di destinazione).
turndown.addRule('wikiLink', {
  filter: (node) =>
    node.nodeName === 'A' &&
    (node.hasAttribute('data-wiki-slug') || node.classList.contains('is-broken')),
  replacement: (content, node) => {
    const el = node as HTMLElement;
    const slug = el.getAttribute('data-wiki-slug') ?? '';
    let label = content.replace(/\\([[\]|])/g, '$1').replace(/\s*⚠\s*$/, '').trim();
    const page = serializePages.get(slug);
    if (page?.icon && label.startsWith(page.icon)) label = label.slice(page.icon.length).trim();
    if (!slug) return label ? `[[${label}]]` : '';
    return !label || label === page?.title ? `[[${slug}]]` : `[[${slug}|${label}]]`;
  },
});

/**
 * Voci di elenco. Sostituisce la regola nativa di turndown per due motivi:
 * il prefisso corto (`- ` invece di `-   `) rende leggibile il sorgente ora che
 * è visibile all'utente, e le checklist prendono qui il marcatore GFM — la
 * casella `<span class="task-box">` è vuota e turndown scarta i nodi vuoti
 * prima ancora di consultare le regole personalizzate.
 */
turndown.addRule('listItem', {
  filter: 'li',
  replacement: (content, node) => {
    const el = node as HTMLElement;
    const done = el.classList.contains('is-done') || !!el.querySelector('.task-box.is-on');
    const task = el.classList.contains('task') ? (done ? '[x] ' : '[ ] ') : '';

    const parent = el.parentNode as HTMLElement | null;
    let prefix = '- ';
    if (parent?.nodeName === 'OL') {
      const start = parent.getAttribute('start');
      const index = Array.prototype.indexOf.call(parent.children, el);
      prefix = `${start ? Number(start) + index : index + 1}. `;
    }

    let body = content.replace(/^\n+/, '').replace(/\n+$/, '\n').replace(/\n/gm, `\n${' '.repeat(prefix.length)}`);
    if (task) body = body.replace(/^\s+/, '');
    return prefix + task + body + (el.nextSibling && !/\n$/.test(body) ? '\n' : '');
  },
});

// Il plugin GFM serializza il barrato con una sola tilde, che markdown-it non
// riconosce: il round-trip perderebbe la formattazione.
turndown.addRule('strikethrough', {
  filter: ['del', 's'],
  replacement: (content) => (content ? `~~${content}~~` : ''),
});

// Il Markdown non ha il sottolineato: si conserva il tag.
turndown.addRule('underline', {
  filter: ['u', 'ins'],
  replacement: (content) => (content ? `<u>${content}</u>` : ''),
});

// Embed video: HTML grezzo su una riga, unico modo per non perderli.
turndown.addRule('embed', {
  filter: (node) => node.nodeName === 'IFRAME' || node.nodeName === 'VIDEO',
  replacement: (_content, node) => block((node as HTMLElement).outerHTML),
});

// Vecchio wrapper `<div class="rte-embed">`: si tiene solo il player interno.
turndown.addRule('embedWrapper', {
  filter: (node) => node.nodeName === 'DIV' && node.classList.contains('rte-embed'),
  replacement: (_content, node) => {
    const inner = (node as HTMLElement).querySelector('iframe, video');
    return inner ? block(inner.outerHTML) : '';
  },
});

/** HTML dell'editor → Markdown da salvare. */
export function htmlToMd(html: string, pages?: WikiPageRef[]): string {
  if (!html || !html.trim()) return '';
  serializePages = new Map((pages ?? []).map((p) => [p.slug, p]));
  try {
    return turndown
      .turndown(html)
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  } finally {
    serializePages = new Map();
  }
}
